import { DevicePhoneMobileIcon } from "@heroicons/react/24/outline";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

import { CreateVideoSchema } from "@mono/contracts";

import { handleApiError } from "@/api/handleApiError";
import { sleep } from "@/api/utils";
import { videoQueryOptions } from "@/api/videos/queries/useVideoQuery";
import { useCreateVideoMutation } from "@/api/videos/useCreateVideoMutation";
import ChatThread, { type ChatMessage } from "@/components/chat/ChatThread";
import toast from "@/components/feedback/toast";
import Button from "@/components/form/Button";
import PromptInput from "@/components/form/PromptInput";
import { BasicLayout } from "@/components/layout/BasicLayout";

export const Route = createFileRoute("/_auth/")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const promptRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const mutation = useCreateVideoMutation();
  const navigate = useNavigate();

  const [submittedPrompt, setSubmittedPrompt] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const form = useForm({
    defaultValues: {
      prompt: "",
      aspectRatio: "16:9",
    } as CreateVideoSchema,
    onSubmit: async ({ value }) => {
      if (isSending) return;

      setIsSending(true);
      setSubmittedPrompt(value.prompt);

      try {
        await sleep(220);

        const data = await mutation.mutateAsync(value);
        const videoId = data.video.id;

        await queryClient.ensureQueryData(videoQueryOptions(videoId));

        void navigate({
          to: "/v/$videoId",
          params: { videoId },
          search: { from: "home" },
        });
      } catch (error) {
        setIsSending(false);
        setSubmittedPrompt(null);
        handleApiError(error);
        toast({
          message: "Nie udało się utworzyć projektu. Spróbuj ponownie.",
          type: "error",
        });
      }
    },
    validators: { onChange: CreateVideoSchema },
  });

  useEffect(() => {
    promptRef.current?.focus();
    const el = promptRef.current;
    if (el) {
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, []);

  const placeholders = useMemo(
    () => [
      "Kobieta w satynowej sukni spaceruje po pustych ulicach Paryża o świcie.",
      "Młody mężczyzna biegnie przez zatłoczone metro w Tokio.",
      "Mgiełka poranka unosi się nad jeziorem w sercu lasu.",
      "Modelka w metalicznej sukni przechadza się po futurystycznym korytarzu pełnym holograficznych świateł.",
    ],
    [],
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [submittedPrompt, isSending]);

  const messages: ChatMessage[] = useMemo(() => {
    const base: ChatMessage[] = [
      {
        id: "init",
        role: "system",
        render: () => "O czym chcesz dziś zrobić film?",
      },
    ];

    if (submittedPrompt) {
      base.push({ id: "prompt", role: "user", render: () => submittedPrompt });
    }

    if (isSending) {
      base.push({
        id: "status",
        role: "system",
        render: () =>
          "Analizuję Twój pomysł i przygotowuję propozycje stylów...",
      });
    }

    return base;
  }, [submittedPrompt, isSending]);

  return (
    <BasicLayout logoShimmer>
      <div className="relative w-full h-full">
        <div className="h-full overflow-auto pb-28 pt-6">
          <ChatThread
            isLoading={isSending}
            messages={messages}
            bottomRef={bottomRef}
          />
        </div>

        <AnimatePresence initial={false}>
          {!isSending && (
            <motion.div
              key="inputbar"
              initial={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-0 left-0 right-0 px-3 pb-safe md:px-6 lg:px-8"
            >
              <div className="mx-auto max-w-2xl bg-panel backdrop-blur-3xl border border-accent/15 rounded-2xl p-3 shadow-xl shadow-accent/5">
                <form
                  className="space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                  }}
                >
                  <form.Field name="prompt">
                    {(field) => (
                      <PromptInput
                        ref={promptRef}
                        name={field.name}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(v) => {
                          field.handleChange(v);
                        }}
                        maxLines={6}
                        placeholders={placeholders}
                        actions={
                          <div className="flex justify-between">
                            <div>
                              <form.Field name="aspectRatio">
                                {(f) => (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                      f.handleChange(
                                        f.state.value === "9:16"
                                          ? "16:9"
                                          : "9:16",
                                      );
                                    }}
                                  >
                                    <span className="flex items-center space-x-2">
                                      <DevicePhoneMobileIcon
                                        className={clsx(
                                          "size-5 duration-500",
                                          f.state.value === "16:9" &&
                                            "rotate-90",
                                        )}
                                      />
                                      <span>{f.state.value}</span>
                                    </span>
                                  </Button>
                                )}
                              </form.Field>
                            </div>

                            <div className="shrink-0">
                              <Button
                                disabled={
                                  mutation.isPending ||
                                  field.state.value.length === 0
                                }
                                isLoading={mutation.isPending}
                                type="submit"
                              >
                                Gen mov!
                              </Button>
                            </div>
                          </div>
                        }
                      />
                    )}
                  </form.Field>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BasicLayout>
  );
}
